import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Curated color themes — each is fully designed to avoid readability issues
// Every theme defines: background, card, foreground, primary, secondary, accent, border, muted
const THEMES = {
  // Default GradLink — Mint & Slate
  default: {
    id: 'default',
    name: 'GradLink Classic',
    preview: ['#E8F4F1', '#1E293B', '#C8E6E0'],
    vars: {
      '--background': '156 33% 93%',
      '--foreground': '215 39% 17%',
      '--card': '0 0% 100%',
      '--card-foreground': '215 39% 17%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '215 39% 17%',
      '--primary': '215 39% 17%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '162 33% 85%',
      '--secondary-foreground': '215 39% 17%',
      '--muted': '215 16% 47%',
      '--muted-foreground': '215 16% 47%',
      '--accent': '162 33% 85%',
      '--accent-foreground': '215 39% 17%',
      '--border': '215 16% 90%',
      '--input': '215 16% 90%',
      '--ring': '162 33% 85%',
      '--mint-fresh': '#C8E6E0',
      '--dark-slate': '#1E293B',
      '--frost-white': '#E8F4F1',
      '--slate-gray': '#64748B',
    },
    bodyBg: '#E8F4F1',
    scrollTrack: '#E8F4F1',
    scrollThumb: '#C8E6E0',
    scrollHover: '#64748B',
  },

  // Dark Mode — True dark with mint accents
  dark: {
    id: 'dark',
    name: 'Dark Mode',
    preview: ['#0f172a', '#e2e8f0', '#2dd4bf'],
    vars: {
      '--background': '222 47% 11%',
      '--foreground': '210 40% 98%',
      '--card': '217 33% 17%',
      '--card-foreground': '210 40% 98%',
      '--popover': '217 33% 17%',
      '--popover-foreground': '210 40% 98%',
      '--primary': '166 84% 40%',
      '--primary-foreground': '222 47% 11%',
      '--secondary': '217 33% 22%',
      '--secondary-foreground': '210 40% 98%',
      '--muted': '215 20% 55%',
      '--muted-foreground': '215 20% 55%',
      '--accent': '166 84% 40%',
      '--accent-foreground': '222 47% 11%',
      '--border': '217 33% 22%',
      '--input': '217 33% 22%',
      '--ring': '166 84% 40%',
      '--mint-fresh': '#2dd4bf',
      '--dark-slate': '#e2e8f0',
      '--frost-white': '#0f172a',
      '--slate-gray': '#94a3b8',
    },
    bodyBg: '#0f172a',
    scrollTrack: '#0f172a',
    scrollThumb: '#1e293b',
    scrollHover: '#2dd4bf',
  },

  // Midnight Blue — Deep navy with cool blue accents
  midnight: {
    id: 'midnight',
    name: 'Midnight Blue',
    preview: ['#0c1222', '#e0e7ff', '#818cf8'],
    vars: {
      '--background': '224 50% 9%',
      '--foreground': '226 100% 94%',
      '--card': '224 42% 15%',
      '--card-foreground': '226 100% 94%',
      '--popover': '224 42% 15%',
      '--popover-foreground': '226 100% 94%',
      '--primary': '234 89% 74%',
      '--primary-foreground': '224 50% 9%',
      '--secondary': '224 42% 20%',
      '--secondary-foreground': '226 100% 94%',
      '--muted': '226 25% 55%',
      '--muted-foreground': '226 25% 55%',
      '--accent': '234 89% 74%',
      '--accent-foreground': '224 50% 9%',
      '--border': '224 42% 20%',
      '--input': '224 42% 20%',
      '--ring': '234 89% 74%',
      '--mint-fresh': '#818cf8',
      '--dark-slate': '#e0e7ff',
      '--frost-white': '#0c1222',
      '--slate-gray': '#94a3b8',
    },
    bodyBg: '#0c1222',
    scrollTrack: '#0c1222',
    scrollThumb: '#1e1b4b',
    scrollHover: '#818cf8',
  },

  // Warm Sand — Warm beige/brown tones
  sand: {
    id: 'sand',
    name: 'Warm Sand',
    preview: ['#faf5ef', '#44403c', '#d97706'],
    vars: {
      '--background': '30 33% 96%',
      '--foreground': '25 10% 25%',
      '--card': '0 0% 100%',
      '--card-foreground': '25 10% 25%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '25 10% 25%',
      '--primary': '25 10% 25%',
      '--primary-foreground': '30 33% 96%',
      '--secondary': '33 80% 90%',
      '--secondary-foreground': '25 10% 25%',
      '--muted': '25 10% 50%',
      '--muted-foreground': '25 10% 50%',
      '--accent': '33 80% 90%',
      '--accent-foreground': '25 10% 25%',
      '--border': '30 20% 88%',
      '--input': '30 20% 88%',
      '--ring': '33 80% 75%',
      '--mint-fresh': '#fbbf24',
      '--dark-slate': '#44403c',
      '--frost-white': '#faf5ef',
      '--slate-gray': '#78716c',
    },
    bodyBg: '#faf5ef',
    scrollTrack: '#faf5ef',
    scrollThumb: '#e7e0d6',
    scrollHover: '#78716c',
  },

  // Royal Purple — Elegant purple tones
  royal: {
    id: 'royal',
    name: 'Royal Purple',
    preview: ['#f5f0ff', '#3b0764', '#a855f7'],
    vars: {
      '--background': '270 50% 97%',
      '--foreground': '273 90% 15%',
      '--card': '0 0% 100%',
      '--card-foreground': '273 90% 15%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '273 90% 15%',
      '--primary': '273 90% 15%',
      '--primary-foreground': '270 50% 97%',
      '--secondary': '270 60% 92%',
      '--secondary-foreground': '273 90% 15%',
      '--muted': '270 20% 55%',
      '--muted-foreground': '270 20% 55%',
      '--accent': '270 60% 92%',
      '--accent-foreground': '273 90% 15%',
      '--border': '270 25% 90%',
      '--input': '270 25% 90%',
      '--ring': '270 60% 80%',
      '--mint-fresh': '#a855f7',
      '--dark-slate': '#3b0764',
      '--frost-white': '#f5f0ff',
      // slate-gray = secondary text color — darker variant for readable AA contrast on pale lavender.
      '--slate-gray': '#5b21b6',
    },
    bodyBg: '#f5f0ff',
    scrollTrack: '#f5f0ff',
    scrollThumb: '#e9d5ff',
    scrollHover: '#7c3aed',
  },

  // Ocean — Cool teal/cyan
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    preview: ['#ecfeff', '#164e63', '#06b6d4'],
    vars: {
      '--background': '183 80% 96%',
      '--foreground': '196 64% 24%',
      '--card': '0 0% 100%',
      '--card-foreground': '196 64% 24%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '196 64% 24%',
      '--primary': '196 64% 24%',
      '--primary-foreground': '183 80% 96%',
      '--secondary': '183 60% 88%',
      '--secondary-foreground': '196 64% 24%',
      '--muted': '196 30% 50%',
      '--muted-foreground': '196 30% 50%',
      '--accent': '183 60% 88%',
      '--accent-foreground': '196 64% 24%',
      '--border': '183 40% 88%',
      '--input': '183 40% 88%',
      '--ring': '183 60% 75%',
      '--mint-fresh': '#06b6d4',
      '--dark-slate': '#164e63',
      '--frost-white': '#ecfeff',
      // Darker teal for AA contrast on pale cyan background.
      '--slate-gray': '#0e7490',
    },
    bodyBg: '#ecfeff',
    scrollTrack: '#ecfeff',
    scrollThumb: '#a5f3fc',
    scrollHover: '#0891b2',
  },

  // Rose — Soft pink/rose tones
  rose: {
    id: 'rose',
    name: 'Rose',
    preview: ['#fff1f2', '#881337', '#fb7185'],
    vars: {
      '--background': '356 100% 97%',
      '--foreground': '343 80% 15%',
      '--card': '0 0% 100%',
      '--card-foreground': '343 80% 15%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '343 80% 15%',
      '--primary': '343 80% 15%',
      '--primary-foreground': '356 100% 97%',
      '--secondary': '356 80% 92%',
      '--secondary-foreground': '343 80% 15%',
      '--muted': '343 30% 50%',
      '--muted-foreground': '343 30% 50%',
      '--accent': '356 80% 92%',
      '--accent-foreground': '343 80% 15%',
      '--border': '356 40% 90%',
      '--input': '356 40% 90%',
      '--ring': '356 80% 80%',
      '--mint-fresh': '#fb7185',
      '--dark-slate': '#881337',
      '--frost-white': '#fff1f2',
      // Darker rose for AA contrast on pale pink background.
      '--slate-gray': '#9f1239',
    },
    bodyBg: '#fff1f2',
    scrollTrack: '#fff1f2',
    scrollThumb: '#fecdd3',
    scrollHover: '#e11d48',
  },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const THEME_LIST = Object.values(THEMES);

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(() => {
    return localStorage.getItem('gl_theme') || 'default';
  });

  const applyTheme = useCallback((id) => {
    const theme = THEMES[id] || THEMES.default;
    const root = document.documentElement;

    // Apply CSS variables
    Object.entries(theme.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply body background
    document.body.style.backgroundColor = theme.bodyBg;

    // Apply scrollbar colors via a dynamic stylesheet
    let styleEl = document.getElementById('gl-theme-scrollbar');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'gl-theme-scrollbar';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      ::-webkit-scrollbar-track { background: ${theme.scrollTrack} !important; }
      ::-webkit-scrollbar-thumb { background: ${theme.scrollThumb} !important; }
      ::-webkit-scrollbar-thumb:hover { background: ${theme.scrollHover} !important; }
    `;

    // Toggle dark class for Tailwind dark: variants
    if (id === 'dark' || id === 'midnight') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  const setTheme = useCallback((id) => {
    if (!THEMES[id]) return;
    const theme = THEMES[id];
    setThemeId(id);
    localStorage.setItem('gl_theme', id);
    // Store hints so the inline pre-boot script can apply them synchronously
    // on the next page load, eliminating the flash-of-default-theme
    localStorage.setItem('gl_theme_bg', theme.bodyBg);
    localStorage.setItem('gl_theme_dark', (id === 'dark' || id === 'midnight') ? '1' : '0');
    applyTheme(id);
  }, [applyTheme]);

  // Apply on mount and when themeId changes.
  // Also refresh the pre-boot localStorage hints so they're always up to date.
  useEffect(() => {
    applyTheme(themeId);
    const theme = THEMES[themeId] || THEMES.default;
    localStorage.setItem('gl_theme_bg', theme.bodyBg);
    localStorage.setItem('gl_theme_dark', (themeId === 'dark' || themeId === 'midnight') ? '1' : '0');
  }, [themeId, applyTheme]);

  const theme = THEMES[themeId] || THEMES.default;
  const isDark = themeId === 'dark' || themeId === 'midnight';

  return (
    <ThemeContext.Provider value={{ theme, themeId, setTheme, isDark, themes: THEME_LIST }}>
      {children}
    </ThemeContext.Provider>
  );
};
